import React, { useContext, useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, Banknote, User } from 'lucide-react';
import { CartContext } from '../CartContext';
import InputField from './InputField';

export default function OrderPage({ setPage, onCompleteOrder }) {
  const { cart, totalPrice, itemCount } = useContext(CartContext);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [amountPaid, setAmountPaid] = useState('');
  const [refNo, setRefNo] = useState('');

  const canConfirm = amountPaid.trim() !== '' && refNo.trim() !== '';

  const [bankInfo, setBankInfo] = useState({
    bankName: '',
    accno: '',
    branch: '',
    ifsc: '',
  });

  useEffect(() => {
    setBankInfo({
      bankName: localStorage.getItem('bankName') || '',
      accno: localStorage.getItem('accno') || '',
      branch: localStorage.getItem('branch') || '',
      ifsc: localStorage.getItem('ifscode') || '',
    });
  }, []);

  const handlePlaceOrder = async () => {
    if (!canConfirm) return;

    const paymentDetails = {
      method: paymentMethod,
      amount: amountPaid,
      reference: refNo,
    };

    try {
      const Podate = new Date().toISOString();
      const OrderTotal = totalPrice;
      const UserName = localStorage.getItem('userName') || 'guest';
      const SessId = localStorage.getItem('sessionId') || 'defaultSession';
      const Vname = localStorage.getItem('vname') || 'Vendor ABC';
      const Vid = parseInt(localStorage.getItem('vid')) || 0;
      const VCode = localStorage.getItem('vcode') || 'V001';
      const NewPoId = 0;
      const DeliveryDate = localStorage.getItem('deliveryDate') || new Date().toISOString().split('T')[0];
      const Address = localStorage.getItem('deliveryAddress') || '';

      const Items = cart.map((item, index) => ({
        Slno: index + 1,
        Itemid: item.id,
        Partno: item.partno || '',
        Qty: item.quantity,
        Rate: item.price,
        Amount: item.price * item.quantity,
        Partdesc: item.name || '',
      }));

      const payload = {
        Podate,
        OrderTotal,
        UserName,
        SessId,
        Vid,
        Vname,
        VCode,
        NewPoId,
        DeliveryDate,
        Address,
        AmountPaid: parseFloat(amountPaid),
        ReferenceNo: refNo,
        Paymode: paymentMethod === 'upi' ? 'upi' : 'bank',
        Items,
      };

      const response = await fetch('http://kngservice.revampapps.com/OnlineCreateSimplePOAPI', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const result = await response.json();
      console.log('Order placed successfully:', result);

      const poid = result?.onlinePoItemBOAPIjs?.Table?.[0]?.Poid;

      if (poid) {
        localStorage.setItem('poid', poid.toString());
        console.log('Stored Poid:', poid);
      } else {
        console.warn('Poid not found in response');
      }

    localStorage.setItem('paymode', paymentMethod === 'upi' ? 'UPI' : 'Bank');
    localStorage.setItem('amountPaid', amountPaid);
    localStorage.setItem('referenceNo', refNo);
    localStorage.setItem('orderCart', JSON.stringify(cart));
    localStorage.setItem('orderTotal', totalPrice.toFixed(2));


      onCompleteOrder(paymentDetails);
    } catch (err) {
      alert('Failed to place order: ' + err.message);
      console.error(err);
    }
  };

  return (
    <div className="container mx-auto max-w-2xl">
      <div className="bg-white p-8 sm:p-12 rounded-2xl shadow-xl">
        <button onClick={() => setPage('cart')} className="flex items-center space-x-2 text-teal-600 hover:text-teal-800 font-medium mb-8">
          <ArrowLeft size={20} />
          <span>Back to Cart</span>
        </button>

        <h1 className="text-3xl font-extrabold text-gray-900 mb-6">Order Summary</h1>

        <div className="space-y-4 mb-6">
          {cart.map(item => (
            <div key={item.id} className="flex justify-between items-center text-gray-700 pb-2 border-b border-gray-100">
              <span>{item.name} <span className="text-gray-500">x {item.quantity}</span></span>
              <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="border-t-2 border-gray-100 pt-6 space-y-3">
          <div className="flex justify-between font-semibold text-gray-600">
            <span>Total Items:</span>
            <span>{itemCount}</span>
          </div>
          <div className="flex justify-between text-2xl font-bold text-gray-900">
            <span>Total Price:</span>
            <span className="text-teal-600">₹{totalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Section */}
        <div className="mt-10 border-t-2 border-gray-100 pt-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Method</h2>

          <div className="flex border border-gray-300 rounded-md mb-6">
            <button
              onClick={() => setPaymentMethod('upi')}
              className={`w-1/2 p-3 font-semibold rounded-l-md transition-colors flex items-center justify-center gap-2 ${paymentMethod === 'upi' ? 'bg-[#46b3a4] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <CreditCard size={20} /> UPI / QR Code
            </button>
            <button
              onClick={() => setPaymentMethod('bank')}
              className={`w-1/2 p-3 font-semibold rounded-r-md transition-colors flex items-center justify-center gap-2 ${paymentMethod === 'bank' ? 'bg-[#46b3a4] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <Banknote size={20} /> Bank Transfer
            </button>
          </div>

          {paymentMethod === 'upi' && (
            <div className="text-center bg-gray-50 p-3 rounded-lg border">
              <p className="mb-4 text-gray-600">Scan the QR Code to pay</p>
              {(() => {
                const baseQr = localStorage.getItem('qrcodelink') || '';
                const updatedQr = baseQr.replace(/am=\d+(\.\d+)?/, `am=${totalPrice.toFixed(2)}`);
                const qrEncoded = encodeURIComponent(updatedQr);
                return (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?data=${qrEncoded}&size=200x200`}
                    alt="QR Code"
                    className="mx-auto rounded-lg shadow-md"
                  />
                );
              })()}
            </div>
          )}

          {paymentMethod === 'bank' && (
            <div className="bg-gray-50 p-6 rounded-lg border space-y-2 text-gray-700">
              <p className="font-semibold text-gray-800">Bank Details:</p>
              <p><strong>Bank Name:</strong> {bankInfo.bankName || 'N/A'}</p>
              <p><strong>Account Number:</strong> {bankInfo.accno || 'N/A'}</p>
              <p><strong>Branch:</strong> {bankInfo.branch || 'N/A'}</p>
              <p><strong>IFSC Code:</strong> {bankInfo.ifsc || 'N/A'}</p>
            </div>
          )}

          <div className="mt-6 space-y-4">
            <InputField icon={<User />} name="amountPaid" type="number" placeholder="Amount Paid" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} required />
            <InputField icon={<User />} name="refNo" type="text" placeholder="Payment Reference No." value={refNo} onChange={(e) => setRefNo(e.target.value)} required />
          </div>
        </div>

        <div className="mt-10">
          <button
            onClick={handlePlaceOrder}
            disabled={!canConfirm}
            className={`w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-6 rounded-lg transition duration-300 text-lg shadow-md hover:shadow-lg ${!canConfirm ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Confirm Order
          </button>

          
        </div>
      </div>
    </div>
  );
}
